import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Skeleton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  TableSortLabel,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Edit, Delete, Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const toErrorMessage = (errorValue, fallbackMessage) => {
  if (!errorValue) return fallbackMessage;
  if (typeof errorValue === "string") return errorValue;
  if (Array.isArray(errorValue)) {
    return errorValue
      .map((item) => toErrorMessage(item, ""))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof errorValue === "object") {
    return Object.entries(errorValue)
      .map(([key, value]) => `${key}: ${toErrorMessage(value, "")}`)
      .filter(Boolean)
      .join(" | ");
  }
  return fallbackMessage;
};

const normalizeCountries = (payload) => {
  if (!Array.isArray(payload)) return [];

  return payload
    .filter((country) => country.name && country.iso2)
    .map((country) => ({
      code: country.iso2,
      name: country.name,
      phonecode: country.phonecode,
      emoji: country.emoji,
    }));
};

const getCountryName = (countries, countryCode) =>
  countries.find((country) => country.code === countryCode)?.name || "";

const unwrapApiPayload = (payload) => payload?.data || payload;

const getFirstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getCountryCode = (country) => {
  if (!country) return "";
  if (typeof country === "string") return country;

  return (
    getFirstValue(
      country.code,
      country.iso2,
      country.iso_2,
      country.country_code
    ) || ""
  );
};

const getCountryLabel = (country) => {
  if (!country || typeof country === "string") return "";

  return (
    getFirstValue(country.name, country.country_name, country.label) || ""
  );
};

const getOrganizationFields = (organization = {}) => {
  const normalizedOrganization = unwrapApiPayload(organization) || {};
  const country =
    normalizedOrganization.country ||
    normalizedOrganization.organization_country ||
    normalizedOrganization.country_details;
  const countryCode =
    getCountryCode(country) ||
    getFirstValue(
      normalizedOrganization.country_code,
      normalizedOrganization.countryCode,
      normalizedOrganization.iso2
    ) ||
    "";
  const countryName =
    getCountryLabel(country) ||
    getFirstValue(
      normalizedOrganization.country_name,
      normalizedOrganization.countryName
    ) ||
    "";

  return {
    organization_name:
      getFirstValue(
        normalizedOrganization.name,
        normalizedOrganization.organization_name,
        normalizedOrganization.organizationName
      ) || "",
    organization_address:
      getFirstValue(
        normalizedOrganization.address,
        normalizedOrganization.organization_address,
        normalizedOrganization.organizationAddress
      ) || "",
    organization_phone_number:
      getFirstValue(
        normalizedOrganization.phone_number,
        normalizedOrganization.phoneNumber,
        normalizedOrganization.organization_phone_number,
        normalizedOrganization.organizationPhoneNumber
      ) || "",
    organization_country_code: countryCode || "",
    organization_country_name: countryName || "",
  };
};

const getOrganizationId = (user = {}) => {
  if (user.organization_id) return user.organization_id;
  if (user.organizationId) return user.organizationId;
  if (!user.organization) return null;
  if (typeof user.organization === "object") {
    return user.organization.id || user.organization._id || null;
  }
  return user.organization;
};

const buildOrganizationPayload = (source, countries) => {
  const countryCode = source.organization_country_code;

  return {
    name: source.organization_name?.trim() || "",
    address: source.organization_address?.trim() || "",
    phone_number: source.organization_phone_number?.trim() || "",
    country: countryCode
      ? {
          code: countryCode,
          name:
            getCountryName(countries, countryCode) ||
            source.organization_country_name ||
            countryCode,
      }
      : null,
  };
};

const SettingsPage = () => {
  const { accessToken } = useAuth();
  const [users, setUsers] = useState([]);
  const [sitesList, setSitesList] = useState([]);
  const [countries, setCountries] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    first_name: "",
    last_name: "",
    site_ids: [],
    user_type: "user", // Replaced is_admin with user_type
    pref_unit_length: "metric",
    pref_unit_temp: "celsius",
    pref_graph_smoothing: true,
    organization_name: "",
    organization_address: "",
    organization_phone_number: "",
    organization_country_code: "",
    organization_country_name: "",
  });
  const [editUser, setEditUser] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [filter, setFilter] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newUserPasswordError, setNewUserPasswordError] = useState("");
  const [editUserPasswordError, setEditUserPasswordError] = useState("");

  const baseURL = process.env.REACT_APP_BASEURL;

  // Fetch users, sites, and current user data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch users (for admins only)
        let userResponse = [];
        if (isAdmin) {
          const response = await axios.get(`${baseURL}/users/`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          userResponse = response.data || [];
          setUsers(userResponse);
        }

        // Fetch sites
        const siteResponse = await axios.get(`${baseURL}/get-sites/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const siteList = siteResponse.data.data || [];

        const countriesResponse = await axios.get(`${baseURL}/countries`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setCountries(normalizeCountries(countriesResponse.data));

        // Fetch current user data for is_admin status and site filtering
        const userDataResponse = await axios.get(`${baseURL}/users/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const userData = userDataResponse.data[0] || {};
        setCurrentUser(userData);
        setIsAdmin(userData.is_admin || false);
        const userSiteIds = userData.sites || [];

        // Filter sites to user-assigned ones
        const userSites = siteList.filter((site) =>
          userSiteIds.includes(site.id)
        );
        setSitesList(userSites);

        // If not admin, set users to only current user
        if (!userData.is_admin) {
          setUsers([userData]);
        }
      } catch (error) {
        console.error(
          "Data fetch error:",
          error.response ? error.response.data : error.message
        );
        setError("Failed to load data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [accessToken, baseURL, isAdmin]);

  // Validate sites input
  const validateSites = (siteIds) => {
    if (!siteIds || siteIds.length === 0) return true;
    return siteIds.every((id) => Number.isInteger(id) && id >= 0);
  };

  const validateOrganization = (organizationSource) => {
    if (
      !organizationSource.organization_name?.trim() ||
      !organizationSource.organization_address?.trim() ||
      !organizationSource.organization_phone_number?.trim() ||
      !organizationSource.organization_country_code
    ) {
      return "Organization name, address, phone number, and country are required.";
    }

    return "";
  };

  const saveOrganization = async (organizationSource, existingOrganizationId) => {
    const organizationPayload = buildOrganizationPayload(
      organizationSource,
      countries
    );
    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };

    if (existingOrganizationId) {
      const response = await axios.patch(
        `${baseURL}/organizations/${existingOrganizationId}`,
        organizationPayload,
        config
      );
      return response.data;
    }

    const response = await axios.post(
      `${baseURL}/organizations`,
      organizationPayload,
      config
    );
    return response.data;
  };

  const fetchOrganizationById = async (organizationId) => {
    if (!organizationId) return null;

    const response = await axios.get(`${baseURL}/organizations/${organizationId}/`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  };

  // Password matching validation for new user
  useEffect(() => {
    if (newUser.confirmPassword) {
      if (newUser.password !== newUser.confirmPassword) {
        setNewUserPasswordError("Passwords do not match.");
      } else {
        setNewUserPasswordError("");
      }
    } else {
      setNewUserPasswordError("");
    }
  }, [newUser.password, newUser.confirmPassword]);

  // Password matching validation for edit user
  useEffect(() => {
    if (editUser && editUser.confirmPassword) {
      if (editUser.password !== editUser.confirmPassword) {
        setEditUserPasswordError("Passwords do not match.");
      } else {
        setEditUserPasswordError("");
      }
    } else if (editUser) {
      setEditUserPasswordError("");
    }
  }, [editUser?.password, editUser?.confirmPassword]);

  // Create user (admin only)
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!isAdmin) {
      setError("You do not have permission to create users.");
      return;
    }

    if (!newUser.username || !newUser.email || !newUser.password) {
      setError("Username, email, and password are required.");
      return;
    }

    if (newUser.password !== newUser.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      setError("Invalid email format.");
      return;
    }

    if (!validateSites(newUser.site_ids)) {
      setError("Selected sites must be valid integers.");
      return;
    }

    const organizationError = validateOrganization(newUser);
    if (organizationError) {
      setError(organizationError);
      return;
    }

    try {
      const organization = await saveOrganization(newUser);
      const organizationId = organization?.id;
      const userTypeMap = {
        admin: { is_admin: true, is_superuser: false, read_only: false },
        superuser: { is_admin: false, is_superuser: true, read_only: false },
        user: { is_admin: false, is_superuser: false, read_only: true },
      };
      const { is_admin, is_superuser, read_only } =
        userTypeMap[newUser.user_type] || userTypeMap.user;

      const payload = {
        ...newUser,
        sites: newUser.site_ids,
        is_admin,
        is_superuser,
        read_only,
      };
      if (organizationId) {
        payload.organization = organizationId;
      }
      delete payload.confirmPassword;
      delete payload.site_ids;
      delete payload.user_type;
      delete payload.organization_name;
      delete payload.organization_address;
      delete payload.organization_phone_number;
      delete payload.organization_country_code;
      delete payload.organization_country_name;

      const response = await axios.post(`${baseURL}/users/`, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setUsers([...users, response.data]);
      setNewUser({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        first_name: "",
        last_name: "",
        site_ids: [],
        user_type: "user",
        pref_unit_length: "metric",
        pref_unit_temp: "celsius",
        pref_graph_smoothing: true,
        organization_name: "",
        organization_address: "",
        organization_phone_number: "",
        organization_country_code: "",
        organization_country_name: "",
      });
      setShowPassword(false);
      setShowConfirmPassword(false);
      setSuccess("User created successfully.");
    } catch (error) {
      setError(
        toErrorMessage(
          error.response?.data?.error || error.response?.data,
          "Failed to create user. Please try again."
        )
      );
    }
  };

  // Update user
  const handleUpdateUser = async () => {
    setError(null);
    setSuccess(null);

    if (!editUser.email) {
      setError("Email is required.");
      return;
    }

    if (editUser.password && editUser.password !== editUser.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editUser.email)) {
      setError("Invalid email format.");
      return;
    }

    if (isAdmin && !validateSites(editUser.site_ids)) {
      setError("Selected sites must be valid integers.");
      return;
    }

    if (isAdmin) {
      const organizationError = validateOrganization(editUser);
      if (organizationError) {
        setError(organizationError);
        return;
      }
    }

    try {
      const existingOrganizationId = getOrganizationId(editUser);
      const organization = isAdmin
        ? await saveOrganization(editUser, existingOrganizationId)
        : null;
      const organizationId = organization?.id || existingOrganizationId;
      const editUserType = editUser.is_admin
        ? "admin"
        : editUser.is_superuser
        ? "superuser"
        : "user";
      const userTypeMap = {
        admin: { is_admin: true, is_superuser: false, read_only: false },
        superuser: { is_admin: false, is_superuser: true, read_only: false },
        user: { is_admin: false, is_superuser: false, read_only: true },
      };
      const rolePayload = isAdmin
        ? userTypeMap[editUserType] || userTypeMap.user
        : {
            is_admin: currentUser.is_admin,
            is_superuser: currentUser.is_superuser,
            read_only: currentUser.read_only,
          };
      const payload = {
        ...editUser,
        sites: isAdmin ? editUser.site_ids : currentUser.sites,
        ...rolePayload,
      };
      if (isAdmin && organizationId) {
        payload.organization = organizationId;
      }
      if (!payload.password) {
        delete payload.password;
      }
      delete payload.confirmPassword;
      delete payload.site_ids;
      delete payload.organization_name;
      delete payload.organization_address;
      delete payload.organization_phone_number;
      delete payload.organization_country_code;
      delete payload.organization_country_name;
      const response = await axios.patch(
        `${baseURL}/users/${editUser.id}/`,
        payload,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      setUsers(
        users.map((user) => (user.id === editUser.id ? response.data : user))
      );
      setCurrentUser((prev) =>
        prev.id === editUser.id ? response.data : prev
      );
      setOpenEditDialog(false);
      setEditUser(null);
      setShowPassword(false);
      setShowConfirmPassword(false);
      setSuccess("User updated successfully.");
    } catch (error) {
      setError(
        toErrorMessage(
          error.response?.data?.error || error.response?.data,
          "Failed to update user. Please try again."
        )
      );
    }
  };

  // Open delete dialog (admin only)
  const handleOpenDeleteDialog = (id) => {
    if (!isAdmin) {
      setError("You do not have permission to delete users.");
      return;
    }
    setDeleteUserId(id);
    setOpenDeleteDialog(true);
  };

  // Delete user (admin only)
  const handleDeleteUser = async () => {
    setError(null);
    setSuccess(null);

    try {
      await axios.delete(`${baseURL}/users/${deleteUserId}/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setUsers(users.filter((user) => user.id !== deleteUserId));
      setSuccess("User deleted successfully.");
    } catch (error) {
      setError(
        toErrorMessage(
          error.response?.data?.error || error.response?.data,
          "Failed to delete user. Please try again."
        )
      );
    } finally {
      setOpenDeleteDialog(false);
      setDeleteUserId(null);
    }
  };

  // Open edit dialog
  const handleOpenEditDialog = async (user) => {
    let organization = user.organization || {
      organization_name: user.organization_name,
      organization_address: user.organization_address || user.street_address,
      organization_phone_number:
        user.organization_phone_number || user.phone_number,
      country: user.organization_country || user.country,
      country_code: user.organization_country_code || user.country_code,
      country_name: user.organization_country_name || user.country_name,
    };
    const organizationId = getOrganizationId(user);

    if (organizationId) {
      try {
        organization = await fetchOrganizationById(organizationId);
      } catch (error) {
        setError(
          toErrorMessage(
            error.response?.data?.error || error.response?.data,
            "Failed to load organization details."
          )
        );
      }
    }

    setEditUser({
      ...user,
      site_ids: user.sites || [],
      password: "",
      confirmPassword: "",
      organization: organizationId || organization,
      ...getOrganizationFields(organization),
    });
    setOpenEditDialog(true);
  };

  // Handle sorting (admin only)
  const handleSort = (field) => {
    const isAsc = sortField === field && sortDirection === "asc";
    setSortField(field);
    setSortDirection(isAsc ? "desc" : "asc");
  };

  // Sorted and filtered users (admin only)
  const sortedAndFilteredUsers = useMemo(() => {
    let filtered = users;
    if (filter && isAdmin) {
      const lowerFilter = filter.toLowerCase();
      filtered = users.filter(
        (user) =>
          user.username.toLowerCase().includes(lowerFilter) ||
          user.email.toLowerCase().includes(lowerFilter)
      );
    }

    return filtered.sort((a, b) => {
      const aValue = a[sortField] ?? "";
      const bValue = b[sortField] ?? "";
      if (sortField === "is_admin") {
        return sortDirection === "asc"
          ? aValue === bValue
            ? 0
            : aValue
            ? -1
            : 1
          : aValue === bValue
          ? 0
          : aValue
          ? 1
          : -1;
      }
      if (typeof aValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
    });
  }, [users, sortField, sortDirection, filter, isAdmin]);

  // Skeleton loader
  const SettingsSkeleton = () => (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Skeleton variant="text" width="30%" height={40} sx={{ mb: 2 }} />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Skeleton variant="rectangular" height={400} sx={{ mb: 2 }} />
        </Grid>
        {isAdmin && (
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
        )}
      </Grid>
    </Box>
  );

  if (loading) return <SettingsSkeleton />;

  return (
    <Box sx={{ px: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {isAdmin ? "User Settings" : "Edit Profile"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Grid container spacing={3} mb={3}>
        {/* Create User Form (Admin Only) */}
        {isAdmin && (
          <Grid item xs={12} md={6} width="100%">
            <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3 }}>
              <Typography variant="h6" gutterBottom>
                Create New User
              </Typography>
              <form onSubmit={handleCreateUser}>
                <Grid container spacing={2} flexWrap="nowrap">
                  <Grid item md={6} width="100%">
                    <TextField
                      label="Username"
                      value={newUser.username}
                      onChange={(e) =>
                        setNewUser({ ...newUser, username: e.target.value })
                      }
                      fullWidth
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item md={6} width="100%">
                    <TextField
                      label="Email"
                      value={newUser.email}
                      onChange={(e) =>
                        setNewUser({ ...newUser, email: e.target.value })
                      }
                      fullWidth
                      margin="normal"
                      required
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} flexWrap="nowrap">
                  <Grid item md={6} width="100%">
                    <TextField
                      label="First Name"
                      value={newUser.first_name}
                      onChange={(e) =>
                        setNewUser({ ...newUser, first_name: e.target.value })
                      }
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item md={6} width="100%">
                    <TextField
                      label="Last Name"
                      value={newUser.last_name}
                      onChange={(e) =>
                        setNewUser({ ...newUser, last_name: e.target.value })
                      }
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} flexWrap="nowrap">
                  <Grid item md={6} width="100%">
                    <TextField
                      label="New Password"
                      type={showPassword ? "text" : "password"}
                      value={newUser.password}
                      onChange={(e) =>
                        setNewUser({ ...newUser, password: e.target.value })
                      }
                      fullWidth
                      margin="normal"
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword ? "Hide password" : "Show password"
                              }
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item md={6} width="100%">
                    <TextField
                      label="Re-enter New Password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={newUser.confirmPassword}
                      onChange={(e) =>
                        setNewUser({
                          ...newUser,
                          confirmPassword: e.target.value,
                        })
                      }
                      fullWidth
                      margin="normal"
                      required
                      error={!!newUserPasswordError}
                      helperText={newUserPasswordError}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showConfirmPassword
                                  ? "Hide confirm password"
                                  : "Show confirm password"
                              }
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} flexWrap="nowrap">
                  <Grid item md={6} width="100%">
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Unit Length</InputLabel>
                      <Select
                        value={newUser.pref_unit_length}
                        onChange={(e) =>
                          setNewUser({
                            ...newUser,
                            pref_unit_length: e.target.value,
                          })
                        }
                        label="Unit Length"
                      >
                        <MenuItem value="metric">Metric</MenuItem>
                        <MenuItem value="imperial">Imperial</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item md={6} width="100%">
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Temperature Unit</InputLabel>
                      <Select
                        value={newUser.pref_unit_temp}
                        onChange={(e) =>
                          setNewUser({
                            ...newUser,
                            pref_unit_temp: e.target.value,
                          })
                        }
                        label="Temperature Unit"
                      >
                        <MenuItem value="celsius">Celsius</MenuItem>
                        <MenuItem value="fahrenheit">Fahrenheit</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid container spacing={2} flexWrap="nowrap">
                  <Grid item md={6} width="100%">
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Sites</InputLabel>
                      <Select
                        multiple
                        value={newUser.site_ids}
                        onChange={(e) =>
                          setNewUser({
                            ...newUser,
                            site_ids: e.target.value,
                          })
                        }
                        label="Sites"
                        renderValue={(selected) =>
                          selected
                            .map((id) => {
                              const site = sitesList.find(
                                (site) => site.id === id
                              );
                              return site
                                ? `Site ${site.id}: ${site.name || `Site ${site.id}`}`
                                : `Site ${id}`;
                            })
                            .join(", ")
                        }
                      >
                        {sitesList.map((site) => (
                          <MenuItem key={site.id} value={site.id}>
                            <Checkbox
                              checked={newUser.site_ids.includes(site.id)}
                            />
                            Site {site.id}: {site.name || `Site ${site.id}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>User Type</InputLabel>
                      <Select
                        value={newUser.user_type}
                        onChange={(e) =>
                          setNewUser({
                            ...newUser,
                            user_type: e.target.value,
                          })
                        }
                        label="User Type"
                      >
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="superuser">SuperUser</MenuItem>
                        <MenuItem value="user">User</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                  Organization Details
                </Typography>
                <Grid container spacing={2} flexWrap="nowrap">
                  <Grid item md={6} width="100%">
                    <TextField
                      label="Organization Name"
                      value={newUser.organization_name}
                      onChange={(e) =>
                        setNewUser({
                          ...newUser,
                          organization_name: e.target.value,
                        })
                      }
                      fullWidth
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item md={6} width="100%">
                    <TextField
                      label="Phone Number"
                      value={newUser.organization_phone_number}
                      onChange={(e) =>
                        setNewUser({
                          ...newUser,
                          organization_phone_number: e.target.value,
                        })
                      }
                      fullWidth
                      margin="normal"
                      required
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} flexWrap="nowrap">
                  <Grid item md={6} width="100%">
                    <TextField
                      label="Organization Address"
                      value={newUser.organization_address}
                      onChange={(e) =>
                        setNewUser({
                          ...newUser,
                          organization_address: e.target.value,
                        })
                      }
                      fullWidth
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item md={6} width="100%">
                    <FormControl fullWidth margin="normal" required>
                      <InputLabel>Country</InputLabel>
                      <Select
                        value={newUser.organization_country_code}
                        onChange={(e) => {
                          const selectedCountry = countries.find(
                            (country) => country.code === e.target.value
                          );
                          setNewUser({
                            ...newUser,
                            organization_country_code: e.target.value,
                            organization_country_name:
                              selectedCountry?.name || "",
                          });
                        }}
                        label="Country"
                      >
                        {newUser.organization_country_code &&
                          !countries.some(
                            (country) =>
                              country.code === newUser.organization_country_code
                          ) && (
                            <MenuItem
                              value={newUser.organization_country_code}
                            >
                              {newUser.organization_country_name ||
                                newUser.organization_country_code}
                            </MenuItem>
                          )}
                        {countries.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.emoji ? `${country.emoji} ` : ""}
                            {country.name} ({country.code})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                  disabled={!!newUserPasswordError}
                >
                  Create User
                </Button>
              </form>
            </Paper>
          </Grid>
        )}

        {/* Edit Profile Form (All Users) */}
        {currentUser && (
          <Grid item xs={12} md={6} width="100%">
            <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3 }}>
              <Typography variant="h6" gutterBottom>
                Edit Profile
              </Typography>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleOpenEditDialog(currentUser);
                }}
              >
                <Grid container spacing={2} flexWrap="nowrap">
                  <Grid item md={6} width="100%">
                    <TextField
                      label="Username"
                      value={currentUser.username || ""}
                      disabled
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item md={6} width="100%">
                    <TextField
                      label="Email"
                      value={editUser?.email || currentUser.email || ""}
                      onChange={(e) =>
                        setEditUser({
                          ...editUser,
                          email: e.target.value,
                          id: currentUser.id,
                          site_ids: currentUser.sites,
                          is_admin: currentUser.is_admin,
                        })
                      }
                      fullWidth
                      margin="normal"
                      required
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} flexWrap="nowrap">
                  <Grid item md={6} width="100%">
                    <TextField
                      label="First Name"
                      value={editUser?.first_name || currentUser.first_name || ""}
                      onChange={(e) =>
                        setEditUser({
                          ...editUser,
                          first_name: e.target.value,
                          id: currentUser.id,
                          site_ids: currentUser.sites,
                          is_admin: currentUser.is_admin,
                        })
                      }
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item md={6} width="100%">
                    <TextField
                      label="Last Name"
                      value={editUser?.last_name || currentUser.last_name || ""}
                      onChange={(e) =>
                        setEditUser({
                          ...editUser,
                          last_name: e.target.value,
                          id: currentUser.id,
                          site_ids: currentUser.sites,
                          is_admin: currentUser.is_admin,
                        })
                      }
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} flexWrap="nowrap">
                  <Grid item md={6} width="100%">
                    <TextField
                      label="New Password"
                      type={showPassword ? "text" : "password"}
                      value={editUser?.password || ""}
                      onChange={(e) =>
                        setEditUser({
                          ...editUser,
                          password: e.target.value,
                          id: currentUser.id,
                          site_ids: currentUser.sites,
                          is_admin: currentUser.is_admin,
                        })
                      }
                      fullWidth
                      margin="normal"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword ? "Hide password" : "Show password"
                              }
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item md={6} width="100%">
                    <TextField
                      label="Re-enter New Password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={editUser?.confirmPassword || ""}
                      onChange={(e) =>
                        setEditUser({
                          ...editUser,
                          confirmPassword: e.target.value,
                          id: currentUser.id,
                          site_ids: currentUser.sites,
                          is_admin: currentUser.is_admin,
                        })
                      }
                      fullWidth
                      margin="normal"
                      error={!!editUserPasswordError}
                      helperText={editUserPasswordError}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showConfirmPassword
                                  ? "Hide confirm password"
                                  : "Show confirm password"
                              }
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} flexWrap="nowrap">
                  <Grid item md={6} width="100%">
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Unit Length</InputLabel>
                      <Select
                        value={
                          editUser?.pref_unit_length ||
                          currentUser.pref_unit_length ||
                          "metric"
                        }
                        onChange={(e) =>
                          setEditUser({
                            ...editUser,
                            pref_unit_length: e.target.value,
                            id: currentUser.id,
                            site_ids: currentUser.sites,
                            is_admin: currentUser.is_admin,
                          })
                        }
                        label="Unit Length"
                      >
                        <MenuItem value="metric">Metric</MenuItem>
                        <MenuItem value="imperial">Imperial</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item md={6} width="100%">
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Temperature Unit</InputLabel>
                      <Select
                        value={
                          editUser?.pref_unit_temp ||
                          currentUser.pref_unit_temp ||
                          "celsius"
                        }
                        onChange={(e) =>
                          setEditUser({
                            ...editUser,
                            pref_unit_temp: e.target.value,
                            id: currentUser.id,
                            site_ids: currentUser.sites,
                            is_admin: currentUser.is_admin,
                          })
                        }
                        label="Temperature Unit"
                      >
                        <MenuItem value="celsius">Celsius</MenuItem>
                        <MenuItem value="fahrenheit">Fahrenheit</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                  disabled={!!editUserPasswordError}
                >
                  Edit Profile
                </Button>
              </form>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Users Table (Admin Only) */}
      {isAdmin && (
        <Grid container spacing={3} pb={3}>
          <Grid item xs={12} width="100%">
            <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Manage Users</Typography>
                <TextField
                  label="Search by Username or Email"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ width: 300 }}
                />
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === "id"}
                          direction={sortField === "id" ? sortDirection : "asc"}
                          onClick={() => handleSort("id")}
                        >
                          ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === "username"}
                          direction={
                            sortField === "username" ? sortDirection : "asc"
                          }
                          onClick={() => handleSort("username")}
                        >
                          Username
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === "email"}
                          direction={sortField === "email" ? sortDirection : "asc"}
                          onClick={() => handleSort("email")}
                        >
                          Email
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === "first_name"}
                          direction={
                            sortField === "first_name" ? sortDirection : "asc"
                          }
                          onClick={() => handleSort("first_name")}
                        >
                          First Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === "last_name"}
                          direction={
                            sortField === "last_name" ? sortDirection : "asc"
                          }
                          onClick={() => handleSort("last_name")}
                        >
                          Last Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === "is_admin"}
                          direction={
                            sortField === "is_admin" ? sortDirection : "asc"
                          }
                          onClick={() => handleSort("is_admin")}
                        >
                          Is Admin
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedAndFilteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.id}</TableCell>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.first_name || "N/A"}</TableCell>
                        <TableCell>{user.last_name || "N/A"}</TableCell>
                        <TableCell>{user.is_admin ? "Yes" : "No"}</TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => handleOpenEditDialog(user)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                          {user.id !== currentUser?.id && (
                            <IconButton
                              onClick={() => handleOpenDeleteDialog(user.id)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Edit User Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {editUser && (
            <>
              <TextField
                label="Username"
                value={editUser.username}
                onChange={(e) =>
                  setEditUser({ ...editUser, username: e.target.value })
                }
                fullWidth
                margin="normal"
                disabled
              />
              <TextField
                label="Email"
                value={editUser.email}
                onChange={(e) =>
                  setEditUser({ ...editUser, email: e.target.value })
                }
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                value={editUser.password}
                onChange={(e) =>
                  setEditUser({ ...editUser, password: e.target.value })
                }
                fullWidth
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Re-enter Password"
                type={showConfirmPassword ? "text" : "password"}
                value={editUser.confirmPassword}
                onChange={(e) =>
                  setEditUser({ ...editUser, confirmPassword: e.target.value })
                }
                fullWidth
                margin="normal"
                error={!!editUserPasswordError}
                helperText={editUserPasswordError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showConfirmPassword
                            ? "Hide confirm password"
                            : "Show confirm password"
                        }
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="First Name"
                value={editUser.first_name}
                onChange={(e) =>
                  setEditUser({ ...editUser, first_name: e.target.value })
                }
                fullWidth
                margin="normal"
              />
              <TextField
                label="Last Name"
                value={editUser.last_name}
                onChange={(e) =>
                  setEditUser({ ...editUser, last_name: e.target.value })
                }
                fullWidth
                margin="normal"
              />
              {isAdmin && (
                <>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Sites</InputLabel>
                    <Select
                      multiple
                      value={editUser.site_ids}
                      onChange={(e) =>
                        setEditUser({
                          ...editUser,
                          site_ids: e.target.value,
                        })
                      }
                      label="Sites"
                      renderValue={(selected) =>
                        selected
                          .map((id) => {
                            const site = sitesList.find(
                              (site) => site.id === id
                            );
                            return site
                              ? `Site ${site.id}: ${site.name || `Site ${site.id}`}`
                              : `Site ${id}`;
                          })
                          .join(", ")
                      }
                    >
                      {sitesList.map((site) => (
                        <MenuItem key={site.id} value={site.id}>
                          <Checkbox
                            checked={editUser.site_ids.includes(site.id)}
                          />
                          Site {site.id}: {site.name || `Site ${site.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editUser.is_admin}
                        onChange={(e) =>
                          setEditUser({
                            ...editUser,
                            is_admin: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Is Admin"
                  />
                </>
              )}
              {isAdmin && (
                <>
                  <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                    Organization Details
                  </Typography>
                  <Grid container spacing={2} flexWrap="nowrap">
                    <Grid item md={6} width="100%">
                      <TextField
                        label="Organization Name"
                        value={editUser.organization_name || ""}
                        onChange={(e) =>
                          setEditUser({
                            ...editUser,
                            organization_name: e.target.value,
                          })
                        }
                        fullWidth
                        margin="normal"
                        required
                      />
                    </Grid>
                    <Grid item md={6} width="100%">
                      <TextField
                        label="Phone Number"
                        value={editUser.organization_phone_number || ""}
                        onChange={(e) =>
                          setEditUser({
                            ...editUser,
                            organization_phone_number: e.target.value,
                          })
                        }
                        fullWidth
                        margin="normal"
                        required
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} flexWrap="nowrap">
                    <Grid item md={6} width="100%">
                      <TextField
                        label="Organization Address"
                        value={editUser.organization_address || ""}
                        onChange={(e) =>
                          setEditUser({
                            ...editUser,
                            organization_address: e.target.value,
                          })
                        }
                        fullWidth
                        margin="normal"
                        required
                      />
                    </Grid>
                    <Grid item md={6} width="100%">
                      <FormControl fullWidth margin="normal" required>
                        <InputLabel>Country</InputLabel>
                        <Select
                          value={editUser.organization_country_code || ""}
                          onChange={(e) => {
                            const selectedCountry = countries.find(
                              (country) => country.code === e.target.value
                            );
                            setEditUser({
                              ...editUser,
                              organization_country_code: e.target.value,
                              organization_country_name:
                                selectedCountry?.name || "",
                            });
                          }}
                          label="Country"
                        >
                          {editUser.organization_country_code &&
                            !countries.some(
                              (country) =>
                                country.code ===
                                editUser.organization_country_code
                            ) && (
                              <MenuItem
                                value={editUser.organization_country_code}
                              >
                                {editUser.organization_country_name ||
                                  editUser.organization_country_code}
                              </MenuItem>
                            )}
                          {countries.map((country) => (
                            <MenuItem key={country.code} value={country.code}>
                              {country.emoji ? `${country.emoji} ` : ""}
                              {country.name} ({country.code})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </>
              )}
              <FormControl fullWidth margin="normal">
                <InputLabel>Unit Length</InputLabel>
                <Select
                  value={editUser.pref_unit_length}
                  onChange={(e) =>
                    setEditUser({
                      ...editUser,
                      pref_unit_length: e.target.value,
                    })
                  }
                  label="Unit Length"
                >
                  <MenuItem value="metric">Metric</MenuItem>
                  <MenuItem value="imperial">Imperial</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Temperature Unit</InputLabel>
                <Select
                  value={editUser.pref_unit_temp}
                  onChange={(e) =>
                    setEditUser({
                      ...editUser,
                      pref_unit_temp: e.target.value,
                    })
                  }
                  label="Temperature Unit"
                >
                  <MenuItem value="celsius">Celsius</MenuItem>
                  <MenuItem value="fahrenheit">Fahrenheit</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateUser}
            variant="contained"
            color="primary"
            disabled={!!editUserPasswordError}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog (Admin Only) */}
      {isAdmin && (
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this user? This action cannot be
              undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button
              onClick={handleDeleteUser}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

    </Box>
  );
};

export default SettingsPage;
